import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import useTranslate from '../../../../../lib/hooks/use-translate'
import {
  InterestEnum,
  interestEnumMapping
} from '../../../../../lib/types/enums/affected-staff-questionnaire/intrest.enum'
import {
  PersonalityEnum,
  personalityEnumMapping
} from '../../../../../lib/types/enums/affected-staff-questionnaire/personality.enum'
import { AffectedStaffQuestionnaireInput } from '../../../../../lib/types/models/affected-staff-questionnaire/affected-staff-questionnaire-input'
import { FormControl, FormField } from '../../../../elements/forms/form'
import ControlledInput from '../../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../../elements/inputs/controlled-radio-input'

const StaffPersonalSpaceQuestionnairePersonalitySection = () => {
  const { t } = useTranslate()
  const form = useFormContext<AffectedStaffQuestionnaireInput>()

  return (
    <InnerBox
      title={t('affected.staff.questionnaire.personality.title')}
      className="p-4 rounded-lg"
    >
      <div className="flex flex-col gap-3">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          {t('affected.staff.questionnaire.personality.closer.to')}
        </Typography>

        <div className="grid grid-cols-2 gap-2">
          {Object.values(PersonalityEnum).map((value) => (
            <Checkbox
              key={value}
              label={t(personalityEnumMapping[value])}
              checked={form.watch('personalityTraits')?.includes(value) || false}
              labelClassName="text-grey-lighter"
              onChange={(checked) => {
                const currentTraits = form.getValues('personalityTraits') || []
                if (checked) {
                  form.setValue('personalityTraits', [...currentTraits, value])
                } else {
                  form.setValue(
                    'personalityTraits',
                    currentTraits.filter((trait) => trait !== value)
                  )
                }
              }}
            />
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t('affected.staff.questionnaire.personality.have.religious.affiliation.question')}
          </Typography>
          <div className="flex items-center gap-4">
            <FormField
              control={form.control}
              name={'hasReligiousAffiliation'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.yes')}
                    checked={field.value === true}
                    onCheck={() =>
                      form.setValue('hasReligiousAffiliation', true, { shouldValidate: true })
                    }
                  />
                </FormControl>
              )}
            />
            <FormField
              control={form.control}
              name={'hasReligiousAffiliation'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.no')}
                    checked={field.value === false}
                    onCheck={() => {
                      form.setValue('hasReligiousAffiliation', false)
                      form.setValue('otherReligiousAffiliation', undefined)
                    }}
                  />
                </FormControl>
              )}
            />
          </div>
          {form.watch('hasReligiousAffiliation') && (
            <FormField
              control={form.control}
              name={'otherReligiousAffiliation'}
              render={({ field }) => (
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="text"
                    label={t('affected.staff.questionnaire.which.one')}
                  />
                </FormControl>
              )}
            />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t('affected.staff.questionnaire.personality.interest')}
          </Typography>
          <div className="grid grid-cols-4 items-center mobile:grid-cols-2 gap-2">
            {Object.values(InterestEnum).map((value) => (
              <Checkbox
                key={value}
                label={t(interestEnumMapping[value])}
                checked={form.watch('interests')?.includes(value) || false}
                labelClassName="text-grey"
                onChange={(checked) => {
                  const currentInterests = form.getValues('interests') || []
                  if (checked) {
                    form.setValue('interests', [...currentInterests, value])
                  } else {
                    form.setValue(
                      'interests',
                      currentInterests.filter((interest) => interest !== value)
                    )
                  }
                }}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t(
              'affected.staff.questionnaire.personality.accept.living.with.family.of.different.ethnic.background.question'
            )}
          </Typography>
          <div className="flex items-center gap-4">
            <FormField
              control={form.control}
              name={'willingToStayWithDifferentEthnicBackground'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.yes')}
                    checked={field.value === true}
                    onCheck={() =>
                      form.setValue('willingToStayWithDifferentEthnicBackground', true, {
                        shouldValidate: true
                      })
                    }
                  />
                </FormControl>
              )}
            />
            <FormField
              control={form.control}
              name={'willingToStayWithDifferentEthnicBackground'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.no')}
                    checked={field.value === false}
                    onCheck={() =>
                      form.setValue('willingToStayWithDifferentEthnicBackground', false, {
                        shouldValidate: true
                      })
                    }
                  />
                </FormControl>
              )}
            />
          </div>
        </div>
      </div>
    </InnerBox>
  )
}

export default StaffPersonalSpaceQuestionnairePersonalitySection
